import { memo, useMemo } from 'react';
import { EvidenceChip } from './EvidenceChip';
import { extractEvidenceIds, stripEvidenceRefs } from '../../utils/staffProfile';
import { cn } from '../../utils/cn';

interface EvidenceTextProps {
  text: string;
  className?: string;
  /** Show the cited evidence as chips after the prose (default). Pass false where chips are listed elsewhere. */
  showChips?: boolean;
}

/**
 * Steward prose with its inline evidence citations (`[ev:n]`, `[ev:n, ev:m]`, bare `ev:n`)
 * removed from the sentence. The cited evidence is shown as chips after the text instead,
 * so the prose reads cleanly while every citation stays one click away.
 */
export const EvidenceText = memo(({ text, className, showChips = true }: EvidenceTextProps) => {
  const prose = useMemo(() => stripEvidenceRefs(text), [text]);
  const ids = useMemo(() => (showChips ? extractEvidenceIds(text) : []), [text, showChips]);
  return (
    <span className={cn('leading-relaxed', className)}>
      <span>{prose}</span>
      {ids.length > 0 && (
        <span className="inline-flex flex-wrap items-center gap-1 ml-1.5 align-middle">
          {ids.map((id) => (
            <EvidenceChip key={id} id={id} />
          ))}
        </span>
      )}
    </span>
  );
});

EvidenceText.displayName = 'EvidenceText';
